import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Search as SearchIcon, Building, MapPin } from "lucide-react";
import { type GeneratedAddress } from '@/types/database';
import { useFuzzySearch } from '@/hooks/useFuzzySearch';
import { cn } from '@/lib/utils';
import { type LocationSearchResult } from '@/types/locationSearch';
import { getCurrentLocation } from '@/utils/locationUtils';
import { getUserCountry, clearCountryCache } from '@/utils/countryCache';
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { usePublicCountry } from '@/contexts/PublicCountryContext';
import {
  buildPlaceSearchAwayNotice,
  type PlaceSearchCountry,
} from '@/utils/placeSearchNotice';
import GetDirectionsPanel from '@/components/search/GetDirectionsPanel';

export type SearchMode = 'address' | 'location' | 'directions';

/**
 * A country is mapped when it is already in the live country list the app loaded.
 * Match the name the app shows, and the code when both sides have one.
 */
function matchesLiveCountry(
  candidate: { code?: string | null; name?: string | null } | null,
  countries: { code?: string | null; name?: string | null }[]
): boolean {
  if (!candidate) {
    return false;
  }
  const code = (candidate.code || '').trim().toUpperCase();
  const name = (candidate.name || '').trim().toLowerCase();
  if (!code && !name) {
    return false;
  }
  return countries.some((item) => {
    const itemCode = String(item.code || '').trim().toUpperCase();
    const itemName = String(item.name || '').trim().toLowerCase();
    return (code.length > 0 && itemCode === code) || (name.length > 0 && itemName === name);
  });
}

interface SearchAutocompleteProps {
  onSelectResult: (result: GeneratedAddress) => void;
  onLocationResult?: (
    result: LocationSearchResult,
    alternatives?: LocationSearchResult[],
    notice?: string | null
  ) => void;
  onSearching?: (isSearching: boolean) => void;
  /** Lets the page hide a previous Place or Address result while Get Directions is open. */
  onSearchModeChange?: (mode: SearchMode) => void;
  placeholder?: string;
}

const liberiaApiBaseUrl = import.meta.env.VITE_LIBERIA_API_BASE_URL?.replace(/\/$/, '');

const mapLiberiaApiLocationResult = (result: any): LocationSearchResult => ({
  type: 'location',
  lat: result.latitude,
  lng: result.longitude,
  name: result.name,
  formattedAddress: result.formattedAddress,
  placeId: result.placeId,
  types: result.types || [],
  businessStatus: result.businessStatus,
  distanceMeters: result.distanceMeters,
});

const SearchAutocomplete = ({ onSelectResult, onLocationResult, onSearching, onSearchModeChange, placeholder }: SearchAutocompleteProps) => {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [isLocationSearch, setIsLocationSearch] = useState(false);
  const [locationError, setLocationError] = useState<string | null>(null);
  // Place hits stay listed until the user picks one. That choice hides the rest.
  const [placeHits, setPlaceHits] = useState<LocationSearchResult[]>([]);
  // Place is the mode shown when the screen opens.
  const [searchMode, setSearchMode] = useState<SearchMode>('location');
  const [detectedCountry, setDetectedCountry] = useState<PlaceSearchCountry | null>(null);
  const [deviceCountryChecked, setDeviceCountryChecked] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { countries, country, resolving } = usePublicCountry();
  
  const { 
    results, 
    isLoading, 
    error,
    searchAddresses 
  } = useFuzzySearch();

  const placeAwayNotice = useMemo(
    () =>
      searchMode === 'location'
        ? buildPlaceSearchAwayNotice(query, detectedCountry, countries)
        : null,
    [searchMode, query, detectedCountry, countries]
  );

  // Learn the device country when the screen opens. Place search already uses
  // this name. Address uses it when that country is not in the live list yet.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const coords = await getCurrentLocation();
        const found = await getUserCountry(coords.lat, coords.lng);
        if (!cancelled && found?.code && found?.name) {
          setDetectedCountry({ code: found.code, name: found.name });
        }
      } catch {
        // GPS is optional; the query can still mention another country.
      } finally {
        if (!cancelled) {
          setDeviceCountryChecked(true);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  // The public country is only set when it is already live. Otherwise use the
  // device country name the app already resolved.
  const countryIsMapped = Boolean(country) || matchesLiveCountry(detectedCountry, countries);
  const unmappedCountryName =
    !resolving && countries.length > 0 && !countryIsMapped && detectedCountry?.name
      ? detectedCountry.name
      : null;
  const addressSearchReady =
    countryIsMapped || (!resolving && deviceCountryChecked && !unmappedCountryName);

  // Keep Place selected when Address cannot be used. Leave the typed text alone.
  useEffect(() => {
    if (!addressSearchReady && searchMode === 'address') {
      setSearchMode('location');
      setIsOpen(false);
      setLocationError(null);
    }
  }, [addressSearchReady, searchMode]);

  const handleSearchModeChange = (value: string) => {
    if (value !== 'address' && value !== 'location' && value !== 'directions') {
      return;
    }
    if (value === 'address' && !addressSearchReady) {
      return;
    }
    // Shared box: switching Place and Address does not clear the text.
    setSearchMode(value);
    setIsOpen(false);
    setLocationError(null);
    setPlaceHits([]);
    setHighlightedIndex(-1);
    onSearchModeChange?.(value);
  };

  // Reduced debug logging for production performance
  if (import.meta.env.DEV) {
    console.log('SearchAutocomplete render - query:', query, 'isOpen:', isOpen, 'isLoading:', isLoading, 'results:', results.length);
  }

  // Perform location search
  const performLocationSearch = useCallback(async (searchQuery: string) => {
    const trimmedQuery = searchQuery.trim();
    if (trimmedQuery.length > 2) {
      setIsOpen(true);
      setIsLocationSearch(true);
      setLocationError(null);
      setPlaceHits([]);
      
      try {
        // Get user's current location to bias search results
        let userLocation = null;
        let userCountry = null;
        
        try {
          const coords = await getCurrentLocation();
          userLocation = { lat: coords.lat, lng: coords.lng };
          
          // Country detection is best-effort only; backend can handle null.
          // Also avoid calling browser-side Geocoding unless explicitly configured.
          userCountry = await getUserCountry(coords.lat, coords.lng);
          if (userCountry?.code && userCountry?.name) {
            setDetectedCountry({ code: userCountry.code, name: userCountry.name });
          }
        } catch (locError) {
          console.log('Could not get user location for biasing, continuing without it');
        }

        // The country selected on the site limits the search. GPS is the fallback.
        const resolvedCountry =
          country?.code && country?.name
            ? { code: country.code, name: country.name }
            : userCountry?.code && userCountry?.name
              ? { code: userCountry.code, name: userCountry.name }
              : detectedCountry;
        const awayNotice = buildPlaceSearchAwayNotice(
          trimmedQuery,
          resolvedCountry,
          countries
        );

        // Do not look up a named country the searcher is not in (e.g. Harper, Liberia).
        if (awayNotice) {
          setLocationError(awayNotice);
          setIsLocationSearch(false);
          setIsOpen(true);
          return;
        }

        let locationResult: LocationSearchResult | null = null;
        let locationAlternatives: LocationSearchResult[] = [];

        if (!liberiaApiBaseUrl) {
          throw new Error('QLS API is not configured');
        }

        const response = await fetch(`${liberiaApiBaseUrl}/api/v1/search/locations`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            query: trimmedQuery,
            userLocation,
            userCountry: resolvedCountry?.code || userCountry?.code || userCountry?.name || null,
          }),
        });

        const payload = await response.json();
        if (!response.ok) {
          throw new Error(payload?.error?.message || 'Failed to search locations');
        }

        if (payload?.data?.primary) {
          locationResult = mapLiberiaApiLocationResult(payload.data.primary);
          locationAlternatives = (payload.data.alternatives || []).map(mapLiberiaApiLocationResult);
        } else {
          const errorMsg = payload?.error?.message || 'Location not found';
          setLocationError(errorMsg);
          setIsLocationSearch(false);
          setIsOpen(true);

          if (errorMsg.includes('in United States') || errorMsg.includes('country')) {
            console.log('Country-related error detected, clearing country cache');
            clearCountryCache();
          }
          return;
        }

        if (locationResult) {
          // Wait for a choice. The other hits disappear once that card is chosen.
          setPlaceHits([locationResult, ...locationAlternatives]);
          setIsOpen(true);
          setIsLocationSearch(false);
        } else {
          const errorMsg = 'Location not found';
          setLocationError(errorMsg);
          setIsLocationSearch(false);
          setIsOpen(true);
        }
      } catch (err: any) {
        console.error('Location search error:', err);
        setLocationError(err.message || 'Failed to find location');
        setIsLocationSearch(false);
      }
    }
  }, [countries, country, detectedCountry]);

  // Debounced search effect
  useEffect(() => {
    const trimmedQuery = query.trim();
    
    // Get Directions uses its own boxes and must not run the shared Address search.
    if (searchMode === 'directions') {
      setIsLocationSearch(false);
      setIsOpen(false);
      return;
    }

    // Place mode waits for Enter. The helper explains country lookup vs a nearby category.
    if (searchMode === 'location') {
      setIsLocationSearch(false);
      setLocationError(null);
      setIsOpen(query.trim().length >= 3);
      return;
    }

    // Regular address search mode
    if (query.length >= 3) {
      const debounceTimer = setTimeout(() => {
        setIsLocationSearch(false);
        setLocationError(null);
        searchAddresses(query);
        setIsOpen(true);
        setHighlightedIndex(-1);
      }, 300);

      return () => clearTimeout(debounceTimer);
    } else {
      setIsOpen(false);
      setHighlightedIndex(-1);
    }
  }, [query, searchAddresses, searchMode]);

  // Update parent about search state
  useEffect(() => {
    if (onSearching) {
      onSearching(isLoading || isLocationSearch);
    }
  }, [isLoading, isLocationSearch, onSearching]);

  // Keyboard navigation and submission
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    const trimmedQuery = query.trim();

    // If location mode, submit on Enter
    if (searchMode === 'location' && e.key === 'Enter') {
      e.preventDefault();
      performLocationSearch(trimmedQuery);
      return;
    }

    if (!isOpen || results.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < results.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev > 0 ? prev - 1 : results.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < results.length) {
          handleSelectResult(results[highlightedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setHighlightedIndex(-1);
        inputRef.current?.blur();
        break;
    }
  }, [query, isOpen, results, highlightedIndex, searchMode, performLocationSearch]);

  // Handle result selection
  const handleSelectResult = useCallback((result: GeneratedAddress) => {
    onSelectResult(result);
    setQuery('');
    setIsOpen(false);
    setHighlightedIndex(-1);
    inputRef.current?.blur();
  }, [onSelectResult]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        !inputRef.current?.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setHighlightedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Format address for display
  const formatDisplayAddress = (address: GeneratedAddress): string => {
    const parts = [
      address.building_number,
      address.street_name,
      address.city
    ].filter(Boolean);
    return parts.join(', ');
  };

  // Get structure info for display
  const getStructureInfo = (address: GeneratedAddress): string | null => {
    if (address.structure_type?.toLowerCase() === 'residential') return null;
    if (address.structure_name) {
      return `${address.structure_name}${address.structure_description ? ` - ${address.structure_description}` : ''}`;
    }
    return null;
  };

  // Highlight matching text
  const highlightMatch = (text: string, query: string): React.ReactNode => {
    if (!query.trim()) return text;
    
    const regex = new RegExp(`(${query.trim().replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);
    
    return parts.map((part, index) => 
      regex.test(part) ? (
        <span key={index} className="bg-yellow-200 text-yellow-900 font-medium">
          {part}
        </span>
      ) : (
        part
      )
    );
  };

  return (
    <div className="relative w-full max-w-2xl">
      {/* Place, Address, then Get Directions. Place stays selected when the screen opens. */}
      <RadioGroup
        value={searchMode}
        onValueChange={handleSearchModeChange}
        className="mb-2 flex flex-row flex-wrap items-start gap-x-6 gap-y-2"
        aria-label="Search type"
      >
        <div className="flex items-center gap-2">
          <RadioGroupItem value="location" id="search-mode-place" />
          <Label htmlFor="search-mode-place" className="font-normal cursor-pointer">
            Place
          </Label>
        </div>
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <RadioGroupItem
              value="address"
              id="search-mode-address"
              disabled={!addressSearchReady}
              aria-describedby={unmappedCountryName ? 'address-search-not-ready' : undefined}
            />
            <Label
              htmlFor="search-mode-address"
              className={cn(
                'font-normal',
                addressSearchReady ? 'cursor-pointer' : 'cursor-not-allowed opacity-70'
              )}
            >
              Address
            </Label>
          </div>
          {unmappedCountryName ? (
            <p id="address-search-not-ready" className="text-xs text-muted-foreground">
              Not ready in {unmappedCountryName} yet.
            </p>
          ) : null}
        </div>
        <div className="flex items-center gap-2">
          <RadioGroupItem value="directions" id="search-mode-directions" />
          <Label htmlFor="search-mode-directions" className="font-normal cursor-pointer">
            Get Directions
          </Label>
        </div>
      </RadioGroup>

      {searchMode === 'directions' ? (
        <GetDirectionsPanel
          countries={countries}
          detectedCountry={detectedCountry}
          selectedCountry={
            country?.code && country?.name ? { code: country.code, name: country.name } : null
          }
          onDetectedCountry={setDetectedCountry}
        />
      ) : (
      <>
      {/* Search Input Container - Completely stable layout using CSS classes */}
      <div className="search-input-container w-full">
          {/* Search Icon - Fixed position */}
          <div className="search-icon-container">
            <SearchIcon className="h-5 w-5 text-gray-400" />
          </div>
          
          {/* Input Field - Fixed dimensions */}
          <input
            ref={inputRef}
            type="text"
            placeholder={
              searchMode === 'location' 
                ? "Place name or nearby category — press Enter"
                : placeholder || "Search by address or property name..."
            }
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              if (placeHits.length > 0) setPlaceHits([]);
            }}
            onKeyDown={handleKeyDown}
            onFocus={() => {
              if (searchMode === 'address' && query.length >= 3 && results.length > 0) {
                setIsOpen(true);
              }
            }}
            className="search-input-field"
            autoComplete="off"
          />
          
          {/* Loading Spinner - Always present, only visible when loading */}
          <div className="search-spinner-container">
            <div className={cn(
              "rounded-full h-4 w-4 border-b-2 border-emerald-600 transition-opacity duration-200",
              (isLoading || isLocationSearch) ? "animate-spin opacity-100" : "opacity-0"
            )}></div>
          </div>
        </div>

      {/* Dropdown Results - Fixed minimum height and stable positioning */}
      {query.length >= 3 && isOpen && (
        <div
          ref={dropdownRef}
          className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-50 max-h-96 overflow-y-auto min-h-[120px]"
        >
          {searchMode === 'location' && placeHits.length > 0 ? (
            <div>
              {placeHits.map((place) => (
                <button
                  key={place.placeId || place.formattedAddress}
                  type="button"
                  className="w-full p-3 text-left border-b border-gray-50 hover:bg-gray-50"
                  onClick={() => {
                    onLocationResult?.(place, [], null);
                    setPlaceHits([]);
                    setIsOpen(false);
                  }}
                >
                  <span className="block text-sm font-medium text-gray-900">{place.name || place.formattedAddress}</span>
                  {place.name ? (
                    <span className="block text-sm text-gray-600">{place.formattedAddress}</span>
                  ) : null}
                </button>
              ))}
            </div>
          ) : (isLoading || isLocationSearch) ? (
            <div className="p-4 text-center min-h-[120px] flex items-center justify-center">
              <div className="flex items-center justify-center space-x-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-emerald-600"></div>
                <span className="text-gray-500 text-sm">
                  {isLocationSearch ? 'Searching location...' : 'Searching...'}
                </span>
              </div>
            </div>
          ) : searchMode === 'location' && (placeAwayNotice || locationError) ? (
            <div className="p-4 text-sm min-h-[120px] flex items-center justify-center text-center px-3">
              <p className="text-amber-800">{placeAwayNotice || locationError}</p>
            </div>
          ) : error || locationError ? (
            <div className="p-4 text-red-600 text-sm min-h-[120px] flex flex-col items-center justify-center space-y-2">
              <div className="text-center">{error || locationError}</div>
              {locationError && (
                <button
                  onClick={() => {
                    clearCountryCache();
                    setLocationError(null);
                    setQuery('');
                  }}
                  className="text-xs text-blue-600 hover:underline"
                >
                  Clear location cache and try again
                </button>
              )}
            </div>
          ) : results.length > 0 ? (
            <>
              <div className="p-2 border-b border-gray-100 text-xs text-gray-500 font-medium">
                {results.length} result{results.length !== 1 ? 's' : ''} found
              </div>
              <div className="min-h-[80px]">
                {results.map((result, index) => {
                  const structureInfo = getStructureInfo(result);
                  return (
                    <div
                      key={result.id}
                      className={cn(
                        "p-3 cursor-pointer border-b border-gray-50 hover:bg-gray-50 transition-colors",
                        highlightedIndex === index && "bg-emerald-50"
                      )}
                      onClick={() => handleSelectResult(result)}
                    >
                      <div className="flex items-start space-x-3">
                        <div className="flex-shrink-0 mt-1">
                          {result.structure_type?.toLowerCase() === 'residential' ? (
                            <MapPin className="h-4 w-4 text-blue-500" />
                          ) : (
                            <Building className="h-4 w-4 text-emerald-500" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          {/* Structure Name/Property Name */}
                          {structureInfo && (
                            <div className="text-sm font-medium text-gray-900 mb-1">
                              {highlightMatch(structureInfo, query)}
                            </div>
                          )}
                          
                          {/* Address */}
                          <div className="text-sm text-gray-600">
                            {highlightMatch(formatDisplayAddress(result), query)}
                          </div>
                          
                          {/* Structure Type Badge */}
                          <div className="mt-1">
                            <span className={cn(
                              "inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium",
                              result.structure_type?.toLowerCase() === 'residential'
                                ? "bg-blue-100 text-blue-800"
                                : "bg-emerald-100 text-emerald-800"
                            )}>
                              {result.structure_type}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          ) : searchMode === 'location' ? (
            <div className="p-4 text-sm min-h-[120px] flex items-center justify-center text-center px-3">
              <p className="text-gray-600">
                A place name is searched across your country, closest first.
                A category such as coffee or pharmacy stays near you.
                Press <span className="font-medium">Enter</span> to search.
              </p>
            </div>
          ) : (
            <div className="p-4 text-gray-500 text-sm text-center min-h-[120px] flex items-center justify-center">
              No addresses found matching "{query}"
            </div>
          )}
        </div>
      )}

      {/* Helper Text - Only show when query is too short in address mode */}
      {searchMode === 'address' && query.length > 0 && query.length < 3 && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-sm z-50 p-2 sm:p-3 min-h-[60px] flex items-center justify-center">
          <div className="text-xs sm:text-sm text-gray-500 text-center px-2">
            Type at least 3 characters to search...
          </div>
        </div>
      )}
      
      {/* Helper Text for location mode */}
      {searchMode === 'location' && query.length > 0 && query.length < 3 && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-sm z-50 p-2 sm:p-3 min-h-[60px] flex items-center justify-center">
          <div className="text-xs sm:text-sm text-gray-500 text-center px-2 leading-relaxed">
            Type 3+ characters and press <span className="font-medium">Enter</span> to search places in your country
          </div>
        </div>
      )}
      </>
      )}
    </div>
  );
};

export default SearchAutocomplete;
